import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePrintFolderLabels } from '../../api/queries';
import { ProblemAlert } from '../../components/ProblemAlert';

/** A szerver 500 fölött `BULK_TOO_LARGE`-ot ad — itt sem engedünk többet. */
const MAX_COUNT = 500;

/**
 * Új mappacímke-köteg: sorszámokat foglal ÉS nyomtatásra küld.
 *
 * A 100-as alapérték a terv döntése: egy kényelmes nyomtatási menet
 * (~100 mp 2 ips-nél), és egy elrontott köteg a ~800-as fizikai tartalék
 * kezelhető töredéke.
 */
export function NewFolderBatchDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const print = usePrintFolderLabels();
  const [count, setCount] = useState('100');
  const [note, setNote] = useState('');

  const countNum = Number(count);
  const valid = Number.isInteger(countNum) && countNum >= 1 && countNum <= MAX_COUNT;

  const close = () => {
    print.reset();
    setCount('100');
    setNote('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>Mappacímkék nyomtatása</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="warning">
            A foglalás a nyomtatás előtt véglegesül: a sorszámok akkor is elhasználódnak, ha a
            nyomtatás elakad. Ez szándékos — így nem fordulhat elő, hogy ugyanaz a szám kétszer
            kerül fizikai címkére. Elakadásnál a köteget <strong>újraküldeni</strong> kell, nem
            újat nyomtatni.
          </Alert>

          <TextField
            label="Darabszám"
            value={count}
            onChange={(e) => setCount(e.target.value.replace(/\D/g, ''))}
            slotProps={{ htmlInput: { inputMode: 'numeric' } }}
            helperText={`1–${MAX_COUNT} db. A rendszer a következő szabad sorszámoktól foglal.`}
            error={count !== '' && !valid}
            fullWidth
          />

          <TextField
            label="Megjegyzés (opcionális)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            fullWidth
          />

          {print.isError && <ProblemAlert error={print.error} />}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Mégse</Button>
        <Button
          variant="contained"
          disabled={!valid || print.isPending}
          onClick={() => {
            print.mutate(
              { count: countNum, note: note.trim() || undefined },
              {
                onSuccess: (batch) => {
                  close();
                  void navigate(`/cimkek/${batch.id}`);
                },
              },
            );
          }}
        >
          Foglalás és nyomtatás
        </Button>
      </DialogActions>
    </Dialog>
  );
}
